import * as React from 'react';
import {
  IDocumentLogUser,
  IUploadDocumentLog,
} from '../../library/DocumentLog';
import { Loader } from '@sportnet/ui/Loader';
import { differenceInCalendarDays, format, formatDistance } from 'date-fns';
import Feed from '@sportnet/ui/Feed';
import bytes2Size from '@sportnet/utilities/bytes2Size';
import formatUserName from '@sportnet/utilities/formatUserName';
import generateEvents from './generateEvents';
import skLocale from 'date-fns/locale/sk';
import styled from '../../theme/styled-components';

const Feed2 = Feed as any;

const StyledFeedEvent = styled(Feed2.Event)`
  margin-bottom: 0 !important;
`;

interface OwnProps {
  log: IUploadDocumentLog;
  onOpenFile: (fileId: string) => void;
  files: any[];
}

type Props = OwnProps;

const Em = styled.span`
  color: ${({ theme }) => theme.color.primary};
`;

const DangerEm = styled.span`
  color: ${({ theme }) => theme.color.danger};
`;

const LeftPadding = styled.div`
  padding-left: 5px;
`;

const File = styled.div`
  margin-bottom: 0.5em;
`;

const LinkLike = styled.span`
  color: ${({ theme }) => theme.color.primary};
  cursor: pointer;
  display: flex;
`;

const StyledExtra = styled(Feed.Extra)`
  font-size: 0.9em;
  padding: 0;
  padding-bottom: 0;
  margin-bottom: 0;
`;

// Styled components for role-based comment highlighting - wraps entire event
const ControleeEventWrapper = styled.div`
  border-left: 4px solid ${({ theme }) => theme.color.primary};
  background-color: ${({ theme }) => theme.color.primary}12;
  margin: 8px 0;
  padding: 12px;
  padding-bottom: 8px;
  border-radius: 6px;
`;

const AuditorEventWrapper = styled.div`
  border-left: 4px solid ${({ theme }) => theme.color.warning};
  background-color: ${({ theme }) => theme.color.warning}12;
  margin: 8px 0;
  padding: 12px;
  padding-bottom: 8px;
  border-radius: 6px;
`;

// Neutral wrapper for comments without specific role
const NeutralEventWrapper = styled.div`
  border-left: 4px solid #9e9e9e;
  background-color: #9e9e9e12;
  margin: 8px 0;
  padding: 12px;
  padding-bottom: 8px;
  border-radius: 6px;
`;

const ControleeUserName = styled.span`
  color: ${({ theme }) => theme.color.primary};
  font-weight: 600;
`;

const AuditorUserName = styled.span`
  color: ${({ theme }) => theme.color.warning};
  font-weight: 600;
`;

const NeutralUserName = styled.span`
  color: #616161;
  font-weight: 600;
`;

const RoleBadge = styled.span<{ $role: 'controlee' | 'auditor' }>`
  display: inline-block;
  font-size: 0.7em;
  padding: 2px 8px;
  border-radius: 3px;
  margin-right: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: ${({ theme, $role }) =>
    $role === 'controlee' ? theme.color.primary : theme.color.warning};
  color: white;
`;

const getRoleLabel = (role: 'controlee' | 'auditor'): string => {
  return role === 'controlee' ? 'Kontrolór' : 'Auditor';
};

// Helper function for backward-compatible user name formatting
const formatUserNameCompat = (user: IDocumentLogUser): string => {
  // New format - use displayName
  if (user.displayName) {
    return user.displayName;
  }
  // Old format - use formatUserName with name/surname
  const formatted = formatUserName(user as { name?: string; surname?: string });
  if (formatted) {
    return formatted;
  }
  // Fallback to _id if no name available
  return user._id || '';
};

const uploadedPlural = (username: string, flag: string, n: number) => {
  if (n === 1) {
    if (!username) {
      return (
        <>
          Nahraný <Em>jeden</Em> nový súbor <DangerEm>{flag}</DangerEm>
        </>
      );
    }
    return (
      <>
        <Em>{username}</Em> nahral <DangerEm>{flag}</DangerEm> <Em>jeden</Em>{' '}
        nový súbor
      </>
    );
  }

  if (n > 1 && n < 5) {
    if (!username) {
      return (
        <>
          Nahrané <Em>{n} nové</Em> súbory <DangerEm>{flag}</DangerEm>
        </>
      );
    }
    return (
      <>
        <Em>{username}</Em> nahral <DangerEm>{flag}</DangerEm> <Em>{n} nové</Em>{' '}
        súbory
      </>
    );
  }

  if (!username) {
    return (
      <>
        Nahraných <Em>{n} nových</Em> súborov <DangerEm>{flag}</DangerEm>
      </>
    );
  }
  return (
    <>
      <Em>{username}</Em> nahral <DangerEm>{flag}</DangerEm> <Em>{n} nových</Em>{' '}
      súborov
    </>
  );
};

const sharedPlural = (
  username: string,
  instanceName: string,
  flag: string,
  n: number
) => {
  if (n === 1) {
    return (
      <>
        Zdieľaný <Em>jeden</Em> nový súbor <DangerEm>{flag}</DangerEm> z konania{' '}
        <Em>{instanceName}</Em>
      </>
    );
  }

  if (n > 1 && n < 5) {
    return (
      <>
        Zdieľané <Em>{n} nové</Em> súbory <DangerEm>{flag}</DangerEm> z konania{' '}
        <Em>{instanceName}</Em>
      </>
    );
  }

  return (
    <>
      Zdieľaných <Em>{n} nových</Em> súborov <DangerEm>{flag}</DangerEm> z
      konania <Em>{instanceName}</Em>
    </>
  );
};

const removedPlural = (username: string, flag: string, n: number) => {
  if (n === 1) {
    if (!username) {
      return (
        <>
          Odstránený <Em>jeden</Em> súbor <DangerEm>{flag}</DangerEm>
        </>
      );
    }
    return (
      <>
        <Em>{username}</Em> odstránil <DangerEm>{flag}</DangerEm> <Em>jeden</Em>{' '}
        súbor
      </>
    );
  }

  if (n > 1 && n < 5) {
    if (!username) {
      return (
        <>
          Odstránené <Em>{n}</Em> súbory <DangerEm>{flag}</DangerEm>
        </>
      );
    }
    return (
      <>
        <Em>{username}</Em> odstránil <DangerEm>{flag}</DangerEm> <Em>{n}</Em>{' '}
        súborov
      </>
    );
  }

  if (!username) {
    return (
      <>
        Odstránených <Em>{n}</Em> súborov <DangerEm>{flag}</DangerEm>
      </>
    );
  }
  return (
    <>
      <Em>{username}</Em> odstránil <DangerEm>{flag}</DangerEm> <Em>{n}</Em>{' '}
      súborov
    </>
  );
};

const DocumentLog: React.FC<Props> = ({ onOpenFile, log, files }) => {
  const [isFetchingFileId, setIsFetchingFileId] = React.useState(null);

  const renderFlagLabel = (flag?: string) => {
    switch (flag) {
      case 'AFTER_ACCEPTATION':
        return 'po schválení';
      case 'AFTER_FAILURE':
        return 'po nesplnení';
      case 'AFTER_DEADLINE':
        return 'po termíne';
      default:
        return '';
    }
  };

  const renderEvent = (
    event: ReturnType<typeof generateEvents>[0],
    idx: number
  ) => {
    if (event.type === 'STATUS_CHANGED') {
      if (event.payload.states[1] === 'OPENED') {
        return null;
      } else if (event.payload.states[1] === 'ACCEPTED') {
        const content = renderEventHelper(
          idx,
          <>
            Dokument bol <Em>akceptovaný</Em>
          </>,
          event.payload.date
        );
        return <NeutralEventWrapper key={idx}>{content}</NeutralEventWrapper>;
      } else if (event.payload.states[1] === 'FAILED') {
        const content = renderEventHelper(
          idx,
          <>
            Dokument bol <Em>zamietnutý</Em>
            {event.payload.note && ' z nasledujúceho dôvodu'}
          </>,
          event.payload.date,
          event.payload.note
        );
        return <NeutralEventWrapper key={idx}>{content}</NeutralEventWrapper>;
      }
    } else if (event.type === 'COMMENT_ADDED') {
      const role = event.payload.user?.role;

      // Render username with role-specific styling and badge before name
      const renderUserName = () => {
        const userName = formatUserNameCompat(event.payload.user);
        if (role === 'controlee') {
          return (
            <>
              <RoleBadge $role="controlee">
                {getRoleLabel('controlee')}
              </RoleBadge>
              <ControleeUserName>{userName}</ControleeUserName>
            </>
          );
        }
        if (role === 'auditor') {
          return (
            <>
              <RoleBadge $role="auditor">{getRoleLabel('auditor')}</RoleBadge>
              <AuditorUserName>{userName}</AuditorUserName>
            </>
          );
        }
        // Neutral style for comments without specific role
        return <NeutralUserName>{userName}</NeutralUserName>;
      };

      const content = renderEventHelper(
        idx,
        <>
          {renderUserName()} pridal{' '}
          {event.payload.flag && (
            <DangerEm>{renderFlagLabel(event.payload.flag)}</DangerEm>
          )}{' '}
          komentár
        </>,
        event.payload.date,
        event.payload.note
      );

      // Wrap entire event in role-specific wrapper
      if (role === 'controlee') {
        return (
          <ControleeEventWrapper key={idx}>{content}</ControleeEventWrapper>
        );
      }
      if (role === 'auditor') {
        return <AuditorEventWrapper key={idx}>{content}</AuditorEventWrapper>;
      }
      // Neutral wrapper for comments without specific role
      return <NeutralEventWrapper key={idx}>{content}</NeutralEventWrapper>;
    } else if (event.type === 'FILES_SHARED') {
      const content = renderEventHelper(
        idx,
        sharedPlural(
          event.payload.user ? formatUserNameCompat(event.payload.user) : '',
          event.payload.originInstance.name,
          renderFlagLabel(event.payload.flag),
          event.payload.files.length
        ),
        event.payload.date,
        event.payload.files.map((file: any, i: any) => (
          <File key={file.fileid}>
            <LinkLike
              onClick={async () => {
                setIsFetchingFileId(file.fileid);
                await onOpenFile(file.fileid);
                setIsFetchingFileId(null);
              }}
            >
              {file.name} ({bytes2Size(file.size)}){' '}
              {isFetchingFileId === file.fileid && (
                <LeftPadding>
                  <Loader size="xs" />
                </LeftPadding>
              )}
            </LinkLike>
          </File>
        ))
      );
      return <NeutralEventWrapper key={idx}>{content}</NeutralEventWrapper>;
    } else if (event.type === 'FILES_ADDED') {
      const content = renderEventHelper(
        idx,
        uploadedPlural(
          event.payload.user ? formatUserNameCompat(event.payload.user) : '',
          renderFlagLabel(event.payload.flag),
          event.payload.files.length
        ),
        event.payload.date,
        event.payload.files.map((file: any, i: any) => (
          <File key={file.fileid}>
            <LinkLike
              onClick={async () => {
                setIsFetchingFileId(file.fileid);
                await onOpenFile(file.fileid);
                setIsFetchingFileId(null);
              }}
            >
              {file.name} ({bytes2Size(file.size)}){' '}
              {isFetchingFileId === file.fileid && (
                <LeftPadding>
                  <Loader size="xs" />
                </LeftPadding>
              )}
            </LinkLike>
          </File>
        ))
      );
      return <NeutralEventWrapper key={idx}>{content}</NeutralEventWrapper>;
    } else if (event.type === 'FILES_REMOVED') {
      const content = renderEventHelper(
        idx,
        removedPlural(
          event.payload.user ? formatUserNameCompat(event.payload.user) : '',
          renderFlagLabel(event.payload.flag),
          (event.payload.files || []).length
        ),
        event.payload.date,
        (event.payload.files || []).map((file: any, i: any) => (
          <File key={file.fileid}>
            <LinkLike
              onClick={async () => {
                setIsFetchingFileId(file.fileid);
                await onOpenFile(file.fileid);
                setIsFetchingFileId(null);
              }}
            >
              {file.name} ({bytes2Size(file.size)}){' '}
              {isFetchingFileId === file.fileid && (
                <LeftPadding>
                  <Loader size="xs" />
                </LeftPadding>
              )}
            </LinkLike>
          </File>
        ))
      );
      return <NeutralEventWrapper key={idx}>{content}</NeutralEventWrapper>;
    } else if (event.type === 'DATE_TO_CHANGED') {
      const content = renderEventHelper(
        idx,
        <>
          Zmena termínu odovzdania na{' '}
          <Em>{format(new Date(event.payload.dateTo), 'd.M.yyyy')}</Em>
        </>,
        event.payload.date
      );
      return <NeutralEventWrapper key={idx}>{content}</NeutralEventWrapper>;
    }
    return null;
  };

  const renderEventHelper = (
    key: number,
    summary: React.ReactNode,
    date: string,
    extra: React.ReactNode = null
  ) => {
    const dateDistance = differenceInCalendarDays(new Date(), new Date(date));
    return (
      <StyledFeedEvent key={key as any}>
        <Feed2.Content>
          <Feed2.Summary>
            {summary}{' '}
            <Feed2.Date
              title={format(new Date(date), 'd.M.yyyy H:mm', {
                locale: skLocale,
              })}
            >
              {dateDistance <= 7
                ? formatDistance(new Date(date), new Date(), {
                    locale: skLocale,
                    addSuffix: true,
                  })
                : format(new Date(date), 'd.M.yyyy H:mm')}
            </Feed2.Date>
          </Feed2.Summary>
          {extra && <StyledExtra>{extra}</StyledExtra>}
        </Feed2.Content>
      </StyledFeedEvent>
    );
  };

  return <Feed>{generateEvents(log, files).map(renderEvent)}</Feed>;
};

export default DocumentLog;
